// Simple toast implementation
export interface ToastOptions {
  duration?: number
  position?: 'top' | 'bottom'
}

export interface Toast {
  id: string
  message: string
  type: 'success' | 'error' | 'info' | 'warning'
  duration: number
  position: 'top' | 'bottom'
}

class ToastManager {
  private toasts: Toast[] = []
  private listeners: ((toasts: Toast[]) => void)[] = []
  private nextId = 0

  private notify() {
    this.listeners.forEach(listener => listener([...this.toasts]))
  }

  private createToast(message: string, type: Toast['type'], options: ToastOptions = {}): Toast {
    const id = `toast-${this.nextId++}`
    const toast: Toast = {
      id,
      message,
      type,
      duration: options.duration || 5000,
      position: options.position || 'top'
    }
    return toast
  }

  private addToast(toast: Toast) {
    this.toasts.push(toast)
    this.notify()

    // Auto remove after duration
    setTimeout(() => {
      this.removeToast(toast.id)
    }, toast.duration)
  }

  private removeToast(id: string) {
    this.toasts = this.toasts.filter(toast => toast.id !== id)
    this.notify()
  }

  success(message: string, options?: ToastOptions) {
    const toast = this.createToast(message, 'success', options)
    this.addToast(toast)
  }

  error(message: string, options?: ToastOptions) {
    const toast = this.createToast(message, 'error', options)
    this.addToast(toast)
  }

  info(message: string, options?: ToastOptions) {
    const toast = this.createToast(message, 'info', options)
    this.addToast(toast)
  }

  warning(message: string, options?: ToastOptions) {
    const toast = this.createToast(message, 'warning', options)
    this.addToast(toast)
  }

  subscribe(listener: (toasts: Toast[]) => void) {
    this.listeners.push(listener)
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener)
    }
  }

  getToasts(): Toast[] {
    return [...this.toasts]
  }
}

export const toast = new ToastManager()
